import { usePageMeta, useFAQSchema } from '@/hooks/useSEO';
import {
  PageHero, TrustBox, DataTable, FrameworkCTA, SectionH2, ILink, useBreadcrumbSchema,
} from './_shared';
import { GratuityCalculatorWidget } from './GratuityCalculatorWidget';

const FAQS = [
  { q: 'Is End-of-Service Gratuity tax-free in Saudi Arabia?', a: 'Yes. ESG payouts are free from personal income tax in Saudi Arabia.' },
  { q: 'How long does an employer have to settle final ESG payouts?', a: 'Under Saudi law, upon termination, the employer must settle all entitlements within 1 week. In case of resignation, settlement must be finalized within 2 weeks.' },
];

const PATH = '/resources/saudi-end-of-service-gratuity-calculator/';

/* Zero-dependency HTML/CSS math primitives */
const Frac = ({ top, bottom }) => (
  <span className="inline-flex flex-col align-middle text-center px-1">
    <span className="border-b-2 border-slate-800 pb-0.5 font-semibold leading-tight">{top}</span>
    <span className="pt-0.5 font-semibold leading-tight">{bottom}</span>
  </span>
);

const FormulaBox = ({ label, children }) => (
  <div className="bg-[#F8F9FA] border-l-4 border-[#000080] rounded-r-lg px-5 py-4 my-5 overflow-x-auto" aria-label={label}>
    <div className="flex items-center flex-wrap gap-2 text-[1.02rem] text-slate-800 leading-relaxed">{children}</div>
  </div>
);

const CasesBlock = ({ label, lead, cases }) => (
  <div className="bg-[#F8F9FA] border-l-4 border-[#000080] rounded-r-lg px-5 py-4 my-5 overflow-x-auto" aria-label={label}>
    <div className="flex items-center flex-wrap gap-2 text-[1.02rem] text-slate-800">
      {lead}
      <span className="inline-flex items-center ml-2">
        <span className="text-5xl font-light leading-none mr-2 select-none" aria-hidden="true">{'{'}</span>
        <span className="grid grid-cols-[auto_auto] gap-x-4 gap-y-2 text-left text-[0.95rem]">
          {cases.map(([value, cond], i) => (
            <span key={i} className="contents">
              <span className="font-semibold">{value}</span>
              <span className="text-slate-600">{cond}</span>
            </span>
          ))}
        </span>
      </span>
    </div>
  </div>
);

/* SPOKE 3 — End-of-Service Gratuity Calculator + Statutory Guide */
export default function SaudiGratuityCalculatorPage() {
  usePageMeta(
    'Saudi End-of-Service Gratuity Calculator & Labor Law Compliance Guide 2026 | Zafco',
    'Statutory End-of-Service Gratuity (ESG) calculator rules under KSA Labor Law Article 84 & 85. Learn how to calculate basic salary, allowance allocations, and Qiwa/Mudad alignment.',
    { canonical: PATH },
  );
  useFAQSchema(FAQS);
  useBreadcrumbSchema([
    { name: 'Home', path: '/' },
    { name: 'Saudi Visa Framework 2026', path: '/saudi-visa-framework-2026/' },
    { name: 'Gratuity Calculator', path: PATH },
  ]);

  return (
    <article data-testid="saudi-gratuity-root" className="bg-white">
      <PageHero
        mid="Saudi Visa Framework 2026"
        crumb="Gratuity Calculator"
        title="Saudi Arabia End-of-Service Gratuity (ESG) Rules & Statutory Calculation Framework (2026)"
      />

      <div className="container mx-auto px-6 max-w-5xl py-12">
        <TrustBox
          author="Zafco Payroll & Statutory Labor Audit Division"
          reviewer="Head of Middle East Legal & Payroll Compliance"
          standard="Saudi Labor Law Articles 84 & 85 (MHRSD / Qiwa Standard)"
          standardLabel="Regulatory Basis"
        />

        <section id="esg-overview">
          <SectionH2 id="esg-overview-h">1. Executive Summary: What is End-of-Service Gratuity?</SectionH2>
          <p className="text-slate-700 leading-relaxed">
            End-of-Service Gratuity (ESG) is a mandatory statutory severance benefit under Saudi Labor Law. Every employer operating in KSA must pay ESG upon employment termination or contract completion. Understanding proper contract structures during initial candidate deployment is key—review our{' '}
            <ILink to="/services/saudi-work-visa-qvp-attestation/" testId="esg-link-qvp">Saudi Work Visa & QVP Guide</ILink> and master{' '}
            <ILink to="/saudi-visa-framework-2026/" testId="esg-link-hub">Saudi Visa Framework 2026</ILink> to ensure contract setup alignment on Qiwa.
          </p>
        </section>

        <GratuityCalculatorWidget />

        <section id="statutory-wage-definition">
          <SectionH2 id="wage-basis-h">2. Statutory Wage Basis for Gratuity Calculations</SectionH2>
          <p className="text-slate-700 leading-relaxed mb-4">
            Under Article 84 of the Saudi Labor Law, End-of-Service Gratuity must be calculated based on the <strong>Last Drawn Actual Wage</strong> (S<sub>f</sub>).
          </p>
          <ul className="list-disc pl-6 space-y-2 text-slate-700 leading-relaxed">
            <li><strong>Included:</strong> Basic Salary + Fixed Monthly Housing Allowance + Fixed Monthly Transportation Allowance.</li>
            <li><strong>Excluded:</strong> Variable sales commissions, discretionary annual bonuses, and overtime allowances (unless contractually defined as a fixed wage component).</li>
          </ul>
        </section>

        <section id="mathematical-formulas">
          <SectionH2 id="formulas-h">3. Formal Statutory Mathematical Formulas</SectionH2>

          <h3 className="text-xl font-bold text-[#000080] font-syne mt-6 mb-3">A. Base Gratuity Calculation (G<sub>base</sub>)</h3>
          <p className="text-slate-700 leading-relaxed">The gross accumulated base gratuity (G<sub>base</sub>) is calculated across two distinct tenure tiers:</p>
          <FormulaBox label="Base Gratuity Calculation Formula">
            <strong>Base Gratuity (G<sub>base</sub>)</strong> = <span>( T<sub>1</sub> × 0.5 × </span>
            <Frac top={<>S<sub>f</sub></>} bottom="12" />
            <span>) + ( T<sub>2</sub> × 1.0 × </span>
            <Frac top={<>S<sub>f</sub></>} bottom="12" />
            <span>)</span>
          </FormulaBox>
          <p className="text-slate-700 leading-relaxed mb-2">Where:</p>
          <ul className="list-disc pl-6 space-y-2 text-slate-700 leading-relaxed">
            <li>S<sub>f</sub> = Total final monthly wage (Basic + Fixed Allowances).</li>
            <li>T<sub>1</sub> = Length of continuous service in months for the first 5 years (up to 60 months).</li>
            <li>T<sub>2</sub> = Length of continuous service in months beyond the 5th year (T<sub>2</sub> = max(0, Total Months − 60)).</li>
          </ul>

          <h3 className="text-xl font-bold text-[#000080] font-syne mt-8 mb-3">B. Termination Multiplier vs. Resignation Scale (Article 85)</h3>
          <p className="text-slate-700 leading-relaxed">
            If employment ends due to <strong>Employer Termination</strong>, <strong>Contract Expiry</strong>, or <strong>Force Majeure</strong>, the employee is entitled to 100% of G<sub>base</sub> (provided service is at least 1 year):
          </p>
          <FormulaBox label="Termination Entitlement Formula">
            <strong>ESG<sub>termination</sub></strong> = 1.0 × G<sub>base</sub>
          </FormulaBox>
          <p className="text-slate-700 leading-relaxed">
            If the employee voluntarily resigns, the statutory payout ratio (R<sub>a</sub>) is scaled per Article 85 based on total service duration:
          </p>
          <FormulaBox label="Resignation Entitlement Formula">
            <strong>ESG<sub>resignation</sub></strong> = R<sub>a</sub> × G<sub>base</sub>
          </FormulaBox>
          <p className="text-slate-700 leading-relaxed">Where the Resignation Adjustment Ratio (R<sub>a</sub>) is defined as:</p>
          <CasesBlock
            label="Resignation Adjustment Scale Cases"
            lead={<strong>Resignation Scale (R<sub>a</sub>)&nbsp;=</strong>}
            cases={[
              ['0%', 'if Tenure < 2 Years'],
              [<span key="a"><Frac top="1" bottom="3" /> (~33.3%)</span>, 'if 2 ≤ Tenure < 5 Years'],
              [<span key="b"><Frac top="2" bottom="3" /> (~66.7%)</span>, 'if 5 ≤ Tenure < 10 Years'],
              ['100%', 'if Tenure ≥ 10 Years'],
            ]}
          />
        </section>

        <section id="worked-examples">
          <SectionH2 id="worked-examples-h">4. Worked Calculation Scenarios</SectionH2>
          <p className="text-slate-700 leading-relaxed mb-4">
            <strong>Scenario Assumptions:</strong> Final monthly base wage S<sub>f</sub> = SAR 12,000 (Basic: SAR 8,000 + Housing: SAR 3,000 + Transport: SAR 1,000). Total service = 6 Years (72 months).
          </p>
          <h3 className="text-lg font-bold text-[#000080] font-syne mb-2">Step 1: Calculate G<sub>base</sub></h3>
          <ul className="list-disc pl-6 space-y-2 text-slate-700 leading-relaxed mb-6">
            <li>First 5 years (60 months): 60 × 0.5 × (12,000 / 12) = 5 × 6,000 = <strong>SAR 30,000</strong></li>
            <li>6th year (12 months): 12 × 1.0 × (12,000 / 12) = 1 × 12,000 = <strong>SAR 12,000</strong></li>
            <li>Total Base Gratuity G<sub>base</sub> = 30,000 + 12,000 = <strong>SAR 42,000</strong></li>
          </ul>
          <h3 className="text-lg font-bold text-[#000080] font-syne mb-2">Step 2: Apply Settlement Scenario</h3>
          <DataTable
            testId="worked-example-table"
            headers={['End-of-Service Reason', 'Applicable Ratio (Rₐ)', 'Final Net Payout']}
            rows={[
              [<strong>Employer Termination / Non-Renewal</strong>, '1.0 (100%)', <strong>SAR 42,000</strong>],
              [<strong>Employee Resignation (6 years tenure)</strong>, '2/3 (~66.67%)', <strong>SAR 28,000</strong>],
            ]}
          />
        </section>

        <section id="mudad-qiwa-alignment">
          <SectionH2 id="mudad-qiwa-h">5. Mudad & Qiwa Contract Alignment Audit</SectionH2>
          <p className="text-slate-700 leading-relaxed">
            Saudi labor authorities cross-verify end-of-service settlement bank transactions uploaded into the <strong>Mudad Wage Protection System (WPS)</strong> against digital employment contracts on <strong>Qiwa</strong>. Any under-reporting of fixed housing or transportation allowances in Mudad will flag non-compliance penalties on the employer's Nitaqat rating. Contract registration workflows are covered in the{' '}
            <ILink to="/services/saudi-work-visa-qvp-attestation/" testId="esg-link-qvp-2">Work Visa & QVP deployment guide</ILink>.
          </p>
        </section>

        <FrameworkCTA />

        <section id="faqs">
          <SectionH2 id="faqs-h">Frequently Asked Questions</SectionH2>
          {FAQS.map((f) => (
            <div key={f.q} className="mb-5">
              <h3 className="text-base font-bold text-[#000080] mb-1.5">{f.q}</h3>
              <p className="text-slate-700 leading-relaxed text-sm">{f.a}</p>
            </div>
          ))}
        </section>
      </div>
    </article>
  );
}
